import './RootErrorBoundary.css';
import type { ComponentChildren } from 'preact';
import { useEffect, useErrorBoundary, useRef, useState } from 'preact/hooks';
import { focusIfKeyboard } from '../../a11y/focus';
import { Button } from '../../components/Button';
import { t } from '../../copy/strings';
import { reloadTv } from '../../state/actions';
import { ui } from '../../state/store';

/** Reset da tela de erro (§12.8). */
export const ROOT_RESET_MS = 5000;
/** Na TV: recarga depois de 8 s, no máximo 3 em 10 min (`reloadTv`, F130). */
export const TV_RELOAD_MS = 8000;

/**
 * Tela com o visual da BootGuard (§12.7): nome do produto (VITE_PRODUCT_NAME, a variável {PRODUCT} do catálogo, como no
 * Brand e na guarda), `error.root` e "Recarregar agora".
 */
function RootErrorScreen() {
  const title = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    focusIfKeyboard(title.current, { scroll: false });
  }, []);
  return (
    <div class="root-error-boundary" role="alert" data-testid="root-error-boundary">
      <h1 ref={title} class="root-error-boundary__title" tabIndex={-1}>
        {import.meta.env.VITE_PRODUCT_NAME}
      </h1>
      <p class="root-error-boundary__text">{t('error.root')}</p>
      <div class="root-error-boundary__actions">
        <Button label={t('error.reload')} onClick={() => location.reload()} />
      </div>
    </div>
  );
}

export interface RootErrorBoundaryProps {
  children?: ComponentChildren;
}

/**
 * Boundary raiz (§12.8): captura qualquer erro fora das boundaries nomeadas e mostra a tela de erro, `role="alert"`.
 * Reseta em 5 s. Na TV (`ui.tv.on`), depois de 8 s chama `reloadTv()` (garante `tv=1` no hash e recarrega, até 3 vezes
 * em 10 min, `sessionStorage['tv-reloads']`); passado o limite, só reseta. `unhandledrejection`: só
 * `console.warn('[app] promessa rejeitada:', reason)`, sem interface. Os sinais ficam fora da árvore: o reset não
 * perde replay, instante nem rota (AC-12.8-04). A LiveRegion fica dentro desta boundary (§15.3).
 */
export function RootErrorBoundary({ children }: RootErrorBoundaryProps) {
  const [failures, setFailures] = useState(0);
  const [error, reset] = useErrorBoundary((err: unknown) => {
    console.error('[app] raiz:', err);
    setFailures((n) => n + 1);
  });

  useEffect(() => {
    const onRejection = (e: PromiseRejectionEvent) => {
      e.preventDefault();
      console.warn('[app] promessa rejeitada:', e.reason);
    };
    window.addEventListener('unhandledrejection', onRejection);
    return () => window.removeEventListener('unhandledrejection', onRejection);
  }, []);

  useEffect(() => {
    if (failures === 0 || error === undefined) return;
    if (ui.peek().tv.on) {
      const id = setTimeout(() => {
        if (!reloadTv()) reset();
      }, TV_RELOAD_MS);
      return () => clearTimeout(id);
    }
    const id = setTimeout(reset, ROOT_RESET_MS);
    return () => clearTimeout(id);
  }, [failures]);

  if (error !== undefined) return <RootErrorScreen />;
  return <>{children}</>;
}
